import React, { useState, Fragment, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser } from "@fortawesome/free-regular-svg-icons";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store/store";
import { setUser, clearUser } from "../../../redux/actions/authActions";
import NavbarItems from "./NavbarItems";
import DarkModeToggle from "./DarkModeToggle";
import MenuToggleIcon from "./MenuToggleIcon";
import AuthModal from "../secondary/AuthModal";
import { auth, db } from "../../firebase";
import "../../css/NavBar.css";
import AccessDeniedModal from "./AccessDeniedModal";
import NoPermissionModal from "./NoPermissionModal";
import { protectedPaths } from "../../../config/versionRoutes";
import { flushSync } from "react-dom";
import { disableDarkMode } from "../../../redux/actions/themeActions";
import { toggleVersion } from "../../../redux/actions/versionActions";

const NavBar: React.FC = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
    const location = useLocation();
	const [isToggled, setToggle] = useState(false);
	const [rotation, setRotation] = useState(0);
	const [showDropdown, setShowDropdown] = useState(false);
    const [showModal, setShowModal] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const { userName, authReady } = useSelector((state: RootState) => state.auth);
    const showAccessModal = useSelector((state: RootState) => state.access.showAccessDeniedModal);
    const showNoPermissionModal = useSelector((state: RootState) => state.access.showNoPermissionModal);
    const version = useSelector((state: RootState) => state.VersionReducer.activeVersion);
    const darkMode = useSelector((state: RootState) => state.theme.darkMode);
    const [maxLength, setMaxLength] = useState(7);

	const navContainerVariants = {
		visible: {
			opacity: 1,
			transition: { x: { velocity: 100 }, duration: 0.3 },
		},
		hidden: {
			opacity: 0,
			transition: { x: { velocity: 100 }, duration: 0.3 },
		},
	};

	const toggleMenu = () => {
		setRotation(!isToggled ? 180 : 0);
		setToggle(prev => !prev);
	};

	const handlePerfilClick = () => {
		const unsubscribe = onAuthStateChanged(auth, (user) => {
			if (user) {
				navigate("/perfil");
			} else {
				setShowModal(true);
			}
			unsubscribe();
		});
	};

	useEffect(() => {
        const url = new URL(window.location.href);
        const logoutManual = sessionStorage.getItem("logoutManual");

        if (!logoutManual && url.pathname === '/' && url.searchParams.get('login') === '1') {
            setShowModal(true);
            url.searchParams.delete('login');
            window.history.replaceState({}, '', url.pathname);
        }

        // Limpia la marca después de comprobarla
        if (logoutManual) {
            sessionStorage.removeItem("logoutManual");
        }

		const unsubscribe = onAuthStateChanged(auth, async (user) => {
			if (user) {
				const docSnap = await getDoc(doc(db, "usuarios", user.uid));
				if (docSnap.exists()) {
					const data = docSnap.data();
					dispatch(setUser(data.nombre || "Usuario"));
				} else {
					dispatch(setUser("Usuario"));
				}
			} else {
				dispatch(clearUser());
			}
		});

		const handleClickOutside = (event: MouseEvent) => {
			if (showDropdown && dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setShowDropdown(false);
			}
		};

        const width = window.innerWidth;

        if (width >= 428) {
            // iPhone 14 Pro Max o pantallas grandes
            setMaxLength(7);
        } else if (width >= 390) {
            // iPhone 12 Pro / 13 / 14
            setMaxLength(4);
        } else {
            // Teléfonos más pequeños
            setMaxLength(3);
        }

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			unsubscribe();
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [dispatch, showDropdown]);

	const handleLogout = async () => {
        sessionStorage.setItem("logoutManual", "true");
		await signOut(auth);
		dispatch(clearUser());
		setShowDropdown(false);

		if (protectedPaths.includes(location.pathname)) {
		    navigate("/");
	    }
	};

    const cambiarAV1 = () => {
        flushSync(() => {
            if (version === "V2" && darkMode) {
                dispatch(disableDarkMode());
            }
        });
        dispatch(toggleVersion());
        navigate("/");
    };

	if (!authReady) return null;

	return (
		<header className="navbar">
			<div className="navbar-container container">
				<div className="row align-items-center w-100">
					<div className="col-2 text-center">
						<div className="navbar-left">
							<div className="boton-hamburguer">
								<MenuToggleIcon isToggled={isToggled} rotation={rotation} toggle={toggleMenu} />
							</div>
						</div>
					</div>

					<div className="col-8 text-center">
						<div className="navbar-center">
							<div className="logo" onClick={() => navigate("/inicio")}>MORRIS Y LUNA</div>
						</div>
					</div>

					<div className={`col-2 text-end ${userName ? "pe-0" : ""}`}>
						<div className={`navbar-right ${userName ? "pe-0" : "pe-1"}`}>
							{userName ? (
								<div className="usuario-dropdown" ref={dropdownRef}>
									<div className="usuario-toggle" onClick={() => setShowDropdown(!showDropdown)}>
										<span className="usuario-nombre">
                                            {userName.length >= maxLength + 3 ? `${userName.slice(0, maxLength)}...` : userName}
										</span>
                                        <ExpandMoreIcon
                                            sx={{
			                                    ml: 0.2,
                                                transition: "transform 0.3s ease",
                                                transform: showDropdown ? "rotate(180deg)" : "rotate(0deg)"
                                            }}
                                        />
									</div>

									<AnimatePresence>
										{showDropdown && (
											<motion.div
												className="usuario-menu"
												initial={{ opacity: 0, scale: 0.2, transformOrigin: "top right" }}
												animate={{ opacity: 1, scale: 1, transformOrigin: "top right" }}
												exit={{
													opacity: 0,
													scale: 0.1,
													transformOrigin: "top right",
													transition: {
														duration: 0.7,
														ease: [0.4, 0, 0.2, 1]
													}
												}}
												transition={{
													duration: 0.35,
													ease: [0.4, 0, 0.2, 1]
												}}
											>
												<button className="btn-mi-perfil" onClick={() => {
													navigate("/perfil");
													setShowDropdown(false);
												}}>
													Mi perfil
												</button>
												<button className="btn-cerrar-sesion" onClick={handleLogout}>
													Cerrar sesión
												</button>
											</motion.div>
										)}
									</AnimatePresence>
								</div>
							) : (
								<span onClick={handlePerfilClick} style={{ cursor: "pointer" }}>
									<FontAwesomeIcon className="icon-navbar pt-1" title="Perfil" icon={faUser} />
								</span>
							)}
						</div>
					</div>
				</div>
			</div>

			<AnimatePresence>
				{isToggled && (
					<Fragment>
						<motion.div
							className="overlay"
							initial={{ opacity: 0 }}
							animate={{ opacity: 0.5 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.3 }}
							onClick={() => {
								setToggle(false);
								setRotation(0);
							}}
						/>
						<motion.nav
							className="drawer"
							initial="hidden"
							animate="visible"
							exit="hidden"
							variants={navContainerVariants}
						>
							<NavbarItems onLinkClick={() => {
                                setToggle(false);
                                setRotation(0);
                            }} />
							<div className="drawer-footer">
                                <div className="enlace-version1" onClick={cambiarAV1}>Primera versión</div>
                                <DarkModeToggle />
                            </div>
						</motion.nav>
					</Fragment>
				)}
			</AnimatePresence>

            {showAccessModal && (
                <AccessDeniedModal onLoginClick={() => setShowModal(true)} />
            )}

            {showNoPermissionModal && (
                <NoPermissionModal />
            )}

            <AuthModal show={showModal} onClose={() => setShowModal(false)} />
        </header>
	);
};

export default NavBar;
